"use client"

import { useEffect, useRef, useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useTranslations } from "next-intl"

import { Button } from "@/components/ui/button"

interface BiomarkerListModalProps {
  open: boolean
  onClose: () => void
  /** Called with the raw pasted list when the visitor submits. */
  onSubmit: (rawList: string) => void
}

/**
 * Modal that collects a pasted biomarker list. On submit it hands the raw text
 * up to the parent, which seeds it into the Ella drawer for the customer-support
 * resolver to answer. Follows the repo's `BookCallModal` idiom (bare
 * `AnimatePresence` + Escape/scroll-lock effect keyed on `open`).
 */
export function BiomarkerListModal({
  open,
  onClose,
  onSubmit,
}: BiomarkerListModalProps) {
  const t = useTranslations("biomarkers.listCheck")
  const [value, setValue] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", handler)
    }
  }, [open, onClose])

  // Focus + reset are keyed on `open` alone, so a parent re-render (the page's
  // scroll listeners churn `onClose`'s identity) can't yank focus back to the
  // textarea or restart the timer. Clearing on close means a reopen starts
  // empty rather than showing the previous paste.
  useEffect(() => {
    if (!open) {
      setValue("")
      return
    }
    const focusTimer = setTimeout(() => textareaRef.current?.focus(), 60)
    return () => clearTimeout(focusTimer)
  }, [open])

  const submit = () => {
    const list = value.trim()
    if (!list) return
    onSubmit(list)
  }

  // Keep Tab focus inside the dialog — `aria-modal` tells assistive tech the
  // background is inert, but without a trap a keyboard user tabs straight into
  // the (visually dimmed) page behind the modal.
  const trapFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return
    const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea, [href], input, [tabindex]:not([tabindex="-1"])'
    )
    if (!focusables || focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    // safe-animate-presence: conditional-mount — child only renders when `open=true`
    <AnimatePresence>
      {open && (
        <>
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <m.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            ref={dialogRef}
            onKeyDown={trapFocus}
            role="dialog"
            aria-modal="true"
            aria-label={t("modalTitle")}
            className="fixed inset-3 z-[101] flex flex-col bg-white p-6 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:p-7"
            style={{
              borderRadius: 24,
              maxWidth: 520,
              width: "calc(100% - 24px)",
              maxHeight: "92vh",
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <h2 className="type-title-lg text-neutral-900">
                {t("modalTitle")}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("close")}
                className="-mt-1 -mr-1 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-colors hover:bg-neutral-200"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M2 2l10 10M12 2L2 12" />
                </svg>
              </button>
            </div>
            <p className="type-body-md mt-2 text-neutral-600">
              {t("modalLead")}
            </p>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit()
              }}
              placeholder={t("placeholder")}
              rows={6}
              maxLength={4000}
              className="type-body-md mt-4 min-h-[132px] flex-1 resize-y rounded-[12px] border border-neutral-200 bg-white p-3 text-neutral-900 transition-[border-color,box-shadow] placeholder:text-neutral-400 focus:border-neutral-400 focus:ring-4 focus:ring-neutral-200/60 focus:outline-none md:flex-none"
            />
            <div className="mt-4 flex justify-end">
              <Button
                type="button"
                variant="primary"
                className="bg-deep-blue-950 hover:bg-deep-blue-900"
                onClick={submit}
                disabled={!value.trim()}
              >
                {t("submit")}
              </Button>
            </div>
          </m.div>
        </>
      )}
    </AnimatePresence>
  )
}
